/**
 * Widget → apps/api client.
 *
 * Every request carries three pieces of identity:
 *   - x-api-key       : the `widgets-host` router key, baked in at build time
 *   - x-app-slug      : `commitment-form` (selects the service identity inside apps/api)
 *   - Authorization   : MP widget user bearer token from <mpp-user-login>
 *
 * The MP token is read from localStorage where MP's WordPress widget stores it
 * under `mpp-widgets_AuthToken`. If the user isn't signed in, that key is empty
 * and our endpoints will 401 — the form's login gate surfaces that.
 */
import { mpFetch } from '@church/nextjs-ui/website/mp-session';

// NEXT_PUBLIC_* vars are for the `next dev` preview (Next exposes these to the
// client). The bare CHURCH_HUB_* vars are what vite bakes into the production
// widget bundle at build time.
const API_BASE =
  process.env.CHURCH_HUB_API_URL ||
  process.env.NEXT_PUBLIC_CHURCH_HUB_API_URL ||
  'https://api.woodsidebible.org';
const API_KEY = process.env.CHURCH_HUB_API_KEY || process.env.NEXT_PUBLIC_CHURCH_HUB_API_KEY || '';
const APP_SLUG = process.env.CHURCH_HUB_APP_SLUG || 'commitment-form';

const MP_TOKEN_STORAGE_KEY = 'mpp-widgets_AuthToken';

export function getMPUserToken(): string | null {
  try {
    const raw = window.localStorage.getItem(MP_TOKEN_STORAGE_KEY);
    // MP's widget sometimes writes the literal string "null" when the user
    // signs out — treat that as no token.
    if (!raw || raw === 'null') return null;
    return raw;
  } catch {
    return null;
  }
}

export function isSignedIn(): boolean {
  const token = getMPUserToken();
  return !!token && token.length > 0;
}

/** Clear the MP token from localStorage. Called when we get a 401 from the
 *  API, so the next isSignedIn() check returns false and the form bounces
 *  back to the signed-out gate. */
export function clearMPToken(): void {
  try {
    window.localStorage.removeItem(MP_TOKEN_STORAGE_KEY);
  } catch {}
}

/** MP's hosted sign-in, coming straight back to this page afterwards. The
 *  widget login flow (`signin-oidc` on my.woodsidebible.org) lands the token
 *  in localStorage and returns to `state`; `mpCustomWidgetAuth=true` is what
 *  MP's own widgets look for. Same URL the profile and serve-finder widgets
 *  build. */
export function mpLoginUrl(returnTo: string = window.location.href): string {
  const host = 'my.woodsidebible.org';
  const state = returnTo + (returnTo.includes('?') ? '&' : '?') + 'mpCustomWidgetAuth=true';
  const nonce = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
    b.toString(16).padStart(2, '0')
  ).join('');
  return (
    `https://${host}/ministryplatformapi/oauth/connect/authorize?response_type=code` +
    `&scope=openid%20http://www.thinkministry.com/dataplatform/scopes/all&client_id=TM.Widgets` +
    `&redirect_uri=https://${host}/widgets/signin-oidc&nonce=${nonce}&state=${encodeURIComponent(state)}`
  );
}

/** Send a signed-out visitor to sign in.
 *
 *  If the host page shows MP's `<mpp-user-login>` element, click it so MP's
 *  own login opens. On woodsidebible.org the header login widget took over
 *  and the stock element is kept only for the OAuth callback, visually
 *  hidden; clicking it did nothing on phones. So a missing or hidden element
 *  goes through MP's hosted sign-in and comes back to this page, where the
 *  token is already in localStorage. */
export function triggerMPLogin(): void {
  try {
    const el = document.querySelector('mpp-user-login') as HTMLElement | null;
    const box = el?.getBoundingClientRect();
    if (el && box && box.width >= 2 && box.height >= 2) {
      const clickable =
        (el.shadowRoot?.querySelector('a, button') as HTMLElement | null) ||
        (el.querySelector('a, button') as HTMLElement | null) ||
        el;
      clickable.click();
      return;
    }
  } catch (err) {
    console.error('[Commitment Form] Failed to trigger MP login:', err);
  }
  window.location.assign(mpLoginUrl());
}

/** Subscribe to MP token changes. Fires when:
 *  - Another tab logs in/out (storage event)
 *  - The window regains focus (covers same-tab login since MP doesn't dispatch
 *    storage events to itself)
 *  - The poll interval ticks (last-resort fallback)
 *
 *  Returns an unsubscribe function. */
export function subscribeToTokenChanges(onChange: (token: string | null) => void): () => void {
  let lastToken = getMPUserToken();

  const check = () => {
    const cur = getMPUserToken();
    if (cur !== lastToken) {
      lastToken = cur;
      onChange(cur);
    }
  };

  const onStorage = (e: StorageEvent) => {
    if (e.key === MP_TOKEN_STORAGE_KEY || e.key === null) check();
  };

  window.addEventListener('storage', onStorage);
  window.addEventListener('focus', check);
  const intervalId = window.setInterval(check, 1500);

  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('focus', check);
    window.clearInterval(intervalId);
  };
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export class ApiError extends Error {
  status: number;
  details: unknown;
  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

async function request<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const token = getMPUserToken();
  if (!token) {
    throw new ApiError(401, 'Not signed in');
  }

  const headers: Record<string, string> = {
    'x-api-key': API_KEY,
    'x-app-slug': APP_SLUG,
    Authorization: `Bearer ${token}`,
  };

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  const res = await mpFetch(`${API_BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body,
    signal: opts.signal,
  });

  if (!res.ok) {
    let parsed: unknown = null;
    try {
      parsed = await res.json();
    } catch {}
    const message = (parsed as { error?: string } | null)?.error ?? `Request failed: ${res.status}`;
    // A 401 means MP rejected the bearer token (expired, invalidated, or the
    // user signed out elsewhere). Clear it so subsequent isSignedIn() checks
    // return false and the form bounces back to the signed-out gate.
    if (res.status === 401) clearMPToken();
    throw new ApiError(res.status, message, parsed);
  }

  return (await res.json()) as T;
}

// --- Typed wrappers ---

export interface Congregation {
  Congregation_ID: number;
  Congregation_Name: string;
  Congregation_Short_Name?: string | null;
  Campus_SVG_URL?: string | null;
}

export interface ProfileResponse {
  contact: {
    contactId: number;
    firstName: string;
    lastName: string;
    email: string | null;
    mobilePhone: string | null;
    householdId: number | null;
    congregationId: number | null;
    imageGuid: string | null;
  };
  spouse: null | {
    contactId: number;
    firstName: string;
    lastName: string;
    email: string | null;
    mobilePhone: string | null;
    imageGuid: string | null;
  };
  congregations: Congregation[];
  existingCommitments: ExistingCommitment[];
}

export interface ExistingCommitment {
  commitmentId: number;
  commitmentDate: string;
  contactId: number;
  previousAnnualGiving: number;
  expandedAnnualGiving: number;
  projectedAnnualGiving: number;
  projectedTwoYearGiving: number;
  storedResourcesGiving: number;
  totalTwoYearCommitment: number;
  includeSpouseGiving: boolean;
  commitmentStatusId: number;
}

export function getProfile(campaignId: number, signal?: AbortSignal): Promise<ProfileResponse> {
  return request<ProfileResponse>(`/api/commitment/profile?campaignId=${campaignId}`, { signal });
}

const MP_FILE_URL =
  process.env.NEXT_PUBLIC_MINISTRY_PLATFORM_FILE_URL ||
  'https://my.woodsidebible.org/ministryplatformapi/files';

export function getContactImageUrl(imageGuid: string | null | undefined): string | null {
  if (!imageGuid) return null;
  if (imageGuid.startsWith('http')) return imageGuid;
  return `${MP_FILE_URL}/${imageGuid}?$thumbnail=true`;
}

export interface CreateCommitmentInput {
  campaignId: number;
  forContactId?: number;
  previousAnnualGiving: number;
  expandedAnnualGiving: number;
  storedResourcesGiving?: number;
  includeSpouse?: boolean;
  spouse?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
}

export interface CreateCommitmentResponse {
  success: true;
  commitmentId: number;
  totalTwoYearCommitment: number;
  projectedAnnualGiving: number;
  projectedTwoYearGiving: number;
}

export function createCommitment(input: CreateCommitmentInput): Promise<CreateCommitmentResponse> {
  return request<CreateCommitmentResponse>('/api/commitment', { method: 'POST', body: input });
}

export interface UpdateStoredResourcesResponse {
  success: true;
  commitmentId: number;
  storedResourcesGiving: number;
  totalTwoYearCommitment: number;
}

export function updateStoredResources(
  commitmentId: number,
  storedResourcesGiving: number
): Promise<UpdateStoredResourcesResponse> {
  return request<UpdateStoredResourcesResponse>('/api/commitment', {
    method: 'PATCH',
    body: { commitmentId, storedResourcesGiving },
  });
}

export interface ReplaceCommitmentInput {
  commitmentId: number;
  previousAnnualGiving: number;
  expandedAnnualGiving: number;
  storedResourcesGiving?: number;
}

export function replaceCommitment(
  input: ReplaceCommitmentInput
): Promise<CreateCommitmentResponse> {
  return request<CreateCommitmentResponse>('/api/commitment', {
    method: 'PUT',
    body: input,
  });
}

export function deleteCommitment(
  commitmentId: number
): Promise<{ success: true; commitmentId: number }> {
  return request(`/api/commitment?commitmentId=${commitmentId}`, { method: 'DELETE' });
}

export interface UpdateContactInput {
  mobilePhone?: string | null;
  congregationId?: number | null;
}

export function updateContact(input: UpdateContactInput) {
  return request<{ success: true; contactId: number }>('/api/commitment/contact', {
    method: 'PATCH',
    body: input,
  });
}
